/**
 * DealerPortal —— 代理店自助页面 (DEALER_ADMIN)，路由 /dealers/me。
 *
 * 含 KPI 卡片、Coupon 列表 + 发码、PromotionCode 列表 + 二维码、月报下载。
 */
import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconCoin, IconFileSpreadsheet, IconPlus, IconQrcode, IconReceipt, IconTrendingUp } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createMyCoupon,
  deactivateMyCoupon,
  deactivateMyPromo,
  exportMyMonthlyReportUrl,
  getMyDealer,
  getMyKpi,
  getMyMonthlyReport,
  listMyCoupons,
  type CouponCreateRequest,
  type CouponDetail,
  type DealerDetail,
  type DealerKpiResponse,
  type MonthlyReportResponse,
  type ReportQuery,
} from '@mygpt/app/apis/services/dealer';
import { Http } from '@mygpt/app/http';

import { CouponCreateModal } from '../Dealers/components/CouponCreateModal';
import { PromoCardItem } from '../Dealers/components/PromoCardItem';
import { QRCodeCard } from '../Dealers/components/QRCodeCard';

const DealerPortalPage = () => {
  const { t } = useTranslation();

  const [dealer, setDealer] = useState<DealerDetail | null>(null);
  const [kpi, setKpi] = useState<DealerKpiResponse | null>(null);
  const [coupons, setCoupons] = useState<CouponDetail[]>([]);
  const [report, setReport] = useState<MonthlyReportResponse | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [issued, setIssued] = useState<CouponDetail | null>(null);

  // v0.9: 查询窗口统一用日期范围，默认当月 1 日~当月末
  const [filterEmail, setFilterEmail] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [dateRange, setDateRange] = useState<DatesRangeValue>(() => [
    dayjs().startOf('month').format('YYYY-MM-DD'),
    dayjs().endOf('month').format('YYYY-MM-DD'),
  ]);

  const reportParams = (): ReportQuery => {
    const p: ReportQuery = {};
    if (dateRange[0] && dateRange[1]) {
      p.start_date = dayjs(dateRange[0]).format('YYYY-MM-DD');
      p.end_date = dayjs(dateRange[1]).format('YYYY-MM-DD');
    }
    if (filterEmail.trim()) p.customer_email = filterEmail.trim();
    if (filterCode.trim()) p.code = filterCode.trim();
    return p;
  };

  const fetchAll = async () => {
    const [d, k, c] = await Promise.all([getMyDealer(), getMyKpi(), listMyCoupons({ page: 1, page_size: 100 })]);
    setDealer(d.data);
    setKpi(k.data);
    setCoupons(c.data.items);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const submit = async (req: CouponCreateRequest) => {
    try {
      const res = await createMyCoupon(req);
      setIssued(res.data);
      setCreateOpen(false);
      await fetchAll();
      notifications.show({ title: t('coupon.issued'), message: res.data.name, color: 'green' });
    } catch (e: any) {
      notifications.show({
        title: 'Error',
        message: e?.response?.data?.detail || String(e),
        color: 'red',
      });
    }
  };

  const fetchReport = async () => {
    const res = await getMyMonthlyReport(reportParams());
    setReport(res.data);
  };

  const downloadReport = async (format: 'xlsx' | 'csv') => {
    const url = exportMyMonthlyReportUrl({ ...reportParams(), format });
    const http = Http.use();
    const resp = await http.get(url, { responseType: 'blob' });
    const blob = new Blob([resp.data]);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const stamp = dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM') : dayjs().format('YYYY-MM');
    a.download = `incentive_report_${dealer?.code}_${stamp}.${format}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // KPI 卡片显示当前月（始终是「本月」），与报表查询范围解耦
  const monthLabel = useMemo(() => dayjs().format('YYYY/MM'), []);

  if (!dealer) {
    return (
      <Container size="lg" py="xl">
        <Text c="dimmed">{t('dealer.loading')}</Text>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Group mb="lg" justify="space-between">
        <div>
          <Group gap="xs">
            <Title order={2}>{dealer.name}</Title>
            {!dealer.is_active && (
              <Badge color="gray" variant="light">
                {t('dealer.inactive')}
              </Badge>
            )}
          </Group>
          <Text c="dimmed">{dealer.code}</Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setCreateOpen(true)}
          disabled={!dealer.is_active}
          title={!dealer.is_active ? (t('dealer.dealer_inactive_hint') as string) : undefined}
        >
          {t('coupon.issue_button')}
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="xl">
        <Card withBorder>
          <Group gap="xs">
            <IconQrcode size={20} />
            <Text size="sm" c="dimmed">
              {t('dealer_portal.kpi.issued')}（{t('dealer_portal.kpi.this_month', { month: monthLabel })}）
            </Text>
          </Group>
          <Title order={2} mt="xs">
            {kpi?.this_month.issued ?? 0}
          </Title>
        </Card>
        <Card withBorder>
          <Group gap="xs">
            <IconReceipt size={20} />
            <Text size="sm" c="dimmed">
              {t('dealer_portal.kpi.contracts')}
            </Text>
          </Group>
          <Title order={2} mt="xs">
            {kpi?.this_month.contracts ?? 0}
          </Title>
        </Card>
        <Card withBorder>
          <Group gap="xs">
            <IconTrendingUp size={20} />
            <Text size="sm" c="dimmed">
              {t('dealer_portal.kpi.incentive')}
            </Text>
          </Group>
          <Title order={2} mt="xs">
            ¥{(kpi?.this_month.incentive ?? 0).toLocaleString()}
          </Title>
        </Card>
      </SimpleGrid>

      <Tabs defaultValue="coupons">
        <Tabs.List>
          <Tabs.Tab value="coupons" leftSection={<IconCoin size={16} />}>
            {t('coupon.list_title')}
          </Tabs.Tab>
          <Tabs.Tab value="report" leftSection={<IconFileSpreadsheet size={16} />}>
            {t('dealer_portal.report.title')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="coupons" pt="md">
          <Stack>
            {coupons.map((c) => (
              <Card key={c.id} withBorder>
                <Stack>
                  <Group justify="space-between">
                    <Box>
                      <Group gap="xs">
                        <Text fw={600}>{c.name}</Text>
                        <Badge color={c.is_active ? 'green' : 'gray'}>
                          {c.is_active ? t('dealer.active') : t('dealer.inactive')}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed">
                        {c.percent_off ? `${c.percent_off}%` : `¥${c.amount_off}`} ·{' '}
                        {t(`coupon.duration.${c.duration}`)}
                      </Text>
                    </Box>
                    {/* Coupon 级停用入口隐藏 —— 1 Coupon = 1 PromotionCode，
                        下面 PromoCardItem 内停用单码即可 */}
                  </Group>

                  {c.promotion_codes.length > 0 && (
                    <Stack gap="xs">
                      {c.promotion_codes.map((p) => (
                        <PromoCardItem
                          key={p.id}
                          promo={p}
                          onDeactivate={async (promoId) => {
                            await deactivateMyPromo(promoId);
                            fetchAll();
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Card>
            ))}
            {coupons.length === 0 && (
              <Text c="dimmed" ta="center" py="lg">
                {t('coupon.empty')}
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="report" pt="md">
          <Group mb="md" align="flex-end" wrap="wrap">
            <DatePickerInput
              type="range"
              label={t('dealer.report_date_range')}
              placeholder={t('dealer.report_date_range_placeholder')}
              valueFormat="YYYY-MM-DD"
              value={dateRange}
              onChange={setDateRange}
              clearable
              w={260}
            />
            <TextInput
              label={t('dealer.report_customer_email')}
              placeholder={t('dealer.report_filter_email_placeholder')}
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.currentTarget.value)}
              w={220}
            />
            <TextInput
              label={t('dealer.report_code')}
              placeholder={t('dealer.report_filter_code_placeholder')}
              value={filterCode}
              onChange={(e) => setFilterCode(e.currentTarget.value)}
              w={180}
            />
            <Button onClick={fetchReport}>{t('dealer.query')}</Button>
            <Button variant="light" onClick={() => downloadReport('xlsx')}>
              {t('dealer_portal.report.download_xlsx')}
            </Button>
          </Group>
          {report && (
            <Card withBorder p={0}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('dealer.report_customer_email')}</Table.Th>
                    <Table.Th>{t('dealer.report_plan')}</Table.Th>
                    <Table.Th>{t('dealer.report_code')}</Table.Th>
                    <Table.Th>{t('dealer.report_contract_date')}</Table.Th>
                    <Table.Th>{t('dealer.report_status')}</Table.Th>
                    <Table.Th>{t('dealer.report_standard')}</Table.Th>
                    <Table.Th>{t('dealer.report_discount')}</Table.Th>
                    <Table.Th>{t('dealer.report_net')}</Table.Th>
                    <Table.Th>{t('dealer.report_incentive')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {report.rows.map((r, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>{r.customer_email}</Table.Td>
                      <Table.Td>{r.plan_name}</Table.Td>
                      <Table.Td>{r.coupon_code}</Table.Td>
                      <Table.Td>
                        {new Date(r.contract_date).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={r.status === 'trial' ? 'gray' : 'green'} variant="light">
                          {r.status === 'trial' ? t('dealer.report_status_trial') : t('dealer.report_status_paid')}
                        </Badge>
                      </Table.Td>
                      <Table.Td>¥{r.standard_amount.toLocaleString('ja-JP')}</Table.Td>
                      <Table.Td>¥{r.discount_amount.toLocaleString('ja-JP')}</Table.Td>
                      <Table.Td>¥{r.net_amount.toLocaleString('ja-JP')}</Table.Td>
                      <Table.Td>¥{r.dealer_incentive_amount.toLocaleString('ja-JP')}</Table.Td>
                    </Table.Tr>
                  ))}
                  {report.rows.length > 0 && (
                    <Table.Tr fw={600} bg="gray.0">
                      <Table.Td colSpan={5}>{t('dealer.report_totals')}</Table.Td>
                      <Table.Td>
                        ¥{report.rows.reduce((s, r) => s + r.standard_amount, 0).toLocaleString('ja-JP')}
                      </Table.Td>
                      <Table.Td>
                        ¥{report.rows.reduce((s, r) => s + r.discount_amount, 0).toLocaleString('ja-JP')}
                      </Table.Td>
                      <Table.Td>¥{report.rows.reduce((s, r) => s + r.net_amount, 0).toLocaleString('ja-JP')}</Table.Td>
                      <Table.Td>
                        ¥{report.rows.reduce((s, r) => s + r.dealer_incentive_amount, 0).toLocaleString('ja-JP')}
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Card>
          )}
        </Tabs.Panel>
      </Tabs>

      <CouponCreateModal opened={createOpen} onClose={() => setCreateOpen(false)} onSubmit={submit} />

      <Modal opened={!!issued} onClose={() => setIssued(null)} title={t('coupon.issued_modal_title')} size="lg">
        {issued && issued.promotion_codes[0] && (
          <Stack align="center">
            <QRCodeCard code={issued.promotion_codes[0].code} url={issued.promotion_codes[0].qr_code_url} />
            <Button onClick={() => setIssued(null)}>{t('coupon.back_to_list')}</Button>
          </Stack>
        )}
      </Modal>
    </Container>
  );
};

export default DealerPortalPage;
